/**
 * motion.js
 * Bold, site-wide motion layer built on GSAP + ScrollTrigger, plus Swiper
 * carousel auto-init. Sections opt in with simple data-attributes so no
 * per-section JS is required:
 *
 *   data-animate="fade-up | fade-in | zoom-in | slide-left | slide-right"
 *   data-animate-delay="0.15"   (seconds, optional)
 *   data-animate-group         (put on a parent to stagger its direct children)
 *
 * Carousels: any element with class "swiper" is auto-initialized. Override
 * defaults per-instance with data-swiper-* attributes (see initSwipers below).
 */
(function () {
  'use strict';

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  var ANIMATION_PRESETS = {
    'fade-up': { from: { opacity: 0, y: 40 }, to: { opacity: 1, y: 0 } },
    'fade-in': { from: { opacity: 0 }, to: { opacity: 1 } },
    'zoom-in': { from: { opacity: 0, scale: 0.85 }, to: { opacity: 1, scale: 1 } },
    'slide-left': { from: { opacity: 0, x: 60 }, to: { opacity: 1, x: 0 } },
    'slide-right': { from: { opacity: 0, x: -60 }, to: { opacity: 1, x: 0 } },
  };

  function initScrollReveal() {
    if (typeof gsap === 'undefined') return;
    if (typeof ScrollTrigger !== 'undefined') gsap.registerPlugin(ScrollTrigger);

    if (prefersReducedMotion()) {
      // Respect the user's OS setting: just make everything visible, no motion.
      document.querySelectorAll('[data-animate]').forEach(function (el) {
        el.style.opacity = 1;
        el.style.transform = 'none';
      });
      return;
    }

    document.querySelectorAll('[data-animate-group]').forEach(function (group) {
      var children = group.querySelectorAll(':scope > [data-animate]');
      animateElements(children, { stagger: 0.12 });
    });

    var singles = document.querySelectorAll('[data-animate]:not([data-animate-group] > [data-animate])');
    animateElements(singles, {});
  }

  function animateElements(elements, opts) {
    elements.forEach(function (el, index) {
      var type = el.dataset.animate || 'fade-up';
      var preset = ANIMATION_PRESETS[type] || ANIMATION_PRESETS['fade-up'];
      var delay = parseFloat(el.dataset.animateDelay || 0) + (opts.stagger ? index * opts.stagger : 0);

      gsap.set(el, preset.from);

      ScrollTrigger.create({
        trigger: el,
        start: 'top 85%',
        once: true,
        onEnter: function () {
          gsap.to(el, Object.assign({}, preset.to, {
            duration: 0.9,
            delay: delay,
            ease: 'power3.out',
          }));
        },
      });
    });
  }

  function initSwipers() {
    if (typeof Swiper === 'undefined') return;

    document.querySelectorAll('.swiper').forEach(function (el) {
      if (el.swiper) return; // already initialized

      new Swiper(el, {
        loop: el.dataset.swiperLoop !== 'false',
        speed: parseInt(el.dataset.swiperSpeed || '600', 10),
        slidesPerView: el.dataset.swiperSlidesPerView || 1,
        spaceBetween: parseInt(el.dataset.swiperSpaceBetween || '16', 10),
        autoplay: el.dataset.swiperAutoplay === 'true'
          ? { delay: parseInt(el.dataset.swiperAutoplayDelay || '4000', 10), disableOnInteraction: false }
          : false,
        pagination: el.querySelector('.swiper-pagination')
          ? { el: el.querySelector('.swiper-pagination'), clickable: true }
          : false,
        navigation: el.querySelector('.swiper-button-next')
          ? { nextEl: el.querySelector('.swiper-button-next'), prevEl: el.querySelector('.swiper-button-prev') }
          : false,
        breakpoints: {
          750: { slidesPerView: el.dataset.swiperSlidesPerViewTablet || el.dataset.swiperSlidesPerView || 2 },
          990: { slidesPerView: el.dataset.swiperSlidesPerViewDesktop || el.dataset.swiperSlidesPerView || 3 },
        },
      });
    });
  }

  ready(function () {
    initScrollReveal();
    initSwipers();
  });

  // Re-run for sections added/edited live in the theme editor.
  document.addEventListener('shopify:section:load', function () {
    initScrollReveal();
    initSwipers();
  });
})();
